import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Package, ShoppingCart, Truck, CheckCircle, AlertTriangle,
  Barcode, X, Play, Check, Search, RefreshCw, ScanLine,
  PackageCheck, Clock, History, ArrowUpRight, Camera,
  Volume2, VolumeX, Plus, FileText, Tag, Box, AlertCircle
} from "lucide-react";
import { wmsService } from "../../services/api";
import BarcodeScanner from "./BarcodeScanner";

const WMSPanel = ({ supplierId }) => {
  // ACHADO DA REVISÃO: o valor padrão era um ID fixo ("supplier-001"),
  // usado silenciosamente se ninguém passasse supplierId - risco real de
  // operar o estoque/pedidos de outro fornecedor por engano. Agora exige
  // que o chamador sempre informe o ID real do fornecedor logado.
  // Estados principais
  const [activeView, setActiveView] = useState('orders'); // orders, picking, history
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Estados da sessão de picking
  const [pickingSession, setPickingSession] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCameraScanner, setShowCameraScanner] = useState(false);

  // Refs
  const barcodeInputRef = useRef(null);
  const audioSuccessRef = useRef(null);
  const audioErrorRef = useRef(null);

  // Carregar dados iniciais
  useEffect(() => {
    loadOrders();
    loadStats();
  }, [supplierId, statusFilter]);

  // Focar no input de código de barras quando em modo picking
  useEffect(() => {
    if (pickingSession && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [pickingSession]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await wmsService.getOrders(supplierId, statusFilter || null);
      setOrders(data);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await wmsService.getStats(supplierId);
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Iniciar separação
  const startPicking = async (order) => {
    try {
      setLoading(true);
      const result = await wmsService.startPicking(order.id);
      setPickingSession(result.session);
      setCurrentOrder(result.order);
      setScanHistory([]);
      setActiveView('picking');
      toast.success(result.message);
    } catch (error) {
      toast.error(error.message || 'Erro ao iniciar separação');
    } finally {
      setLoading(false);
    }
  };

  // Processar código de barras
  const handleBarcodeScan = async (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      e.preventDefault();
      await processScan(barcodeInput.trim());
      setBarcodeInput('');
    }
  };

  const processScan = async (barcode) => {
    if (!pickingSession) return;

    try {
      const result = await wmsService.scanBarcode(pickingSession.id, barcode);
      
      // Adicionar ao histórico
      setScanHistory(prev => [{
        barcode,
        ...result,
        timestamp: new Date().toLocaleTimeString('pt-BR')
      }, ...prev]);

      // Tocar som
      if (soundEnabled) {
        if (result.success) {
          playSuccessSound();
        } else {
          playErrorSound();
        }
      }

      // Atualizar pedido atual
      if (result.success) {
        setCurrentOrder(prev => ({
          ...prev,
          picked_items: (prev.picked_items || 0) + 1,
          items: prev.items.map(item => 
            item.product_name === result.product_name
              ? { ...item, picked_qty: result.picked_qty }
              : item
          )
        }));

        // Verificar se pedido completo
        if (result.order_complete) {
          toast.success('🎉 Todos os itens foram separados!');
        }
      }

      // Mostrar resultado
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }

    } catch (error) {
      toast.error(error.message || 'Erro ao processar código');
      if (soundEnabled) playErrorSound();
    }
  };

  // Finalizar separação
  const finishPicking = async () => {
    if (!pickingSession) return;

    try {
      setLoading(true);
      const result = await wmsService.finishPicking(pickingSession.id);
      toast.success(result.message);
      
      // Limpar estado
      setPickingSession(null);
      setCurrentOrder(null);
      setScanHistory([]);
      setActiveView('orders');
      
      // Recarregar dados
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.message || 'Erro ao finalizar separação');
    } finally {
      setLoading(false);
    }
  };

  // Cancelar separação
  const cancelPicking = () => {
    setPickingSession(null);
    setCurrentOrder(null);
    setScanHistory([]);
    setActiveView('orders');
    setShowCameraScanner(false);
    toast.info('Separação cancelada');
  };

  // Handler para código escaneado pela câmera
  const handleCameraScan = (code) => {
    processScan(code);
  };

  // Marcar como enviado
  const shipOrder = async (orderId) => {
    try {
      const trackingCode = prompt('Código de rastreio (opcional):');
      const result = await wmsService.shipOrder(orderId, trackingCode);
      toast.success(result.message);
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.message || 'Erro ao marcar como enviado');
    }
  };

  // Sons
  const playSuccessSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleWs9dJPCxreGYTJCfbLU0amKdWNUhKjL0bWIcmxRiqrM0bGEZ3BbkqjL0a+IdGZGgqbJwqJ/aWJrn8bVxp54T0mGttu6kF45R4y12tCujVpBTI6x2tGrkFdDWo2y2tOpjlNESZO02dSnilJITo6y2dKojFA=');
      audio.volume = 0.3;
      audio.play();
    } catch (e) {}
  };

  const playErrorSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRl9vT19teleWs9dJPCxreGYTJCfbLU0amKdWNUhKjL0bWIcmxRiqrM0bGEZ3BbkqjL0a+IdGZGgqbJwqJ/aWJrn8bVxp54T0mGttu6kF45R4y12tCujVpBTI6x2tGrkFdDWo2y2tOpjlNESZO02dSnilJITo6y2dKojFA=');
      audio.volume = 0.3;
      audio.play();
    } catch (e) {}
  };

  // Status badge
  const getStatusBadge = (status) => {
    const configs = {
      A_SEPARAR: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "A Separar", icon: Clock },
      EM_SEPARACAO: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Em Separação", icon: ScanLine },
      SEPARADO: { bg: "bg-green-500/20", text: "text-green-400", label: "Separado", icon: PackageCheck },
      ENVIADO: { bg: "bg-purple-500/20", text: "text-purple-400", label: "Enviado", icon: Truck },
      CANCELADO: { bg: "bg-red-500/20", text: "text-red-400", label: "Cancelado", icon: X }
    };
    const config = configs[status] || configs.A_SEPARAR;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header com Estatísticas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-yellow-500/10 border-yellow-500/30 p-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-gray-400 text-xs">A Separar</p>
                <p className="text-yellow-400 text-xl font-bold">{stats.pedidos?.a_separar || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30 p-3">
            <div className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-gray-400 text-xs">Separados</p>
                <p className="text-green-400 text-xl font-bold">{stats.pedidos?.separados || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30 p-3">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-gray-400 text-xs">Enviados</p>
                <p className="text-purple-400 text-xl font-bold">{stats.pedidos?.enviados || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30 p-3">
            <div className="flex items-center gap-2">
              <Barcode className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-gray-400 text-xs">Bipagens Hoje</p>
                <p className="text-blue-400 text-xl font-bold">{stats.bipagens_hoje || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modo de Picking Ativo */}
      {activeView === 'picking' && pickingSession && currentOrder && (
        <div className="space-y-4">
          {/* Header da Separação */}
          <Card className="bg-gradient-to-r from-orange-500/20 to-orange-600/20 border-orange-500/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center animate-pulse">
                  <ScanLine className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">
                    Separando Pedido #{currentOrder.order_number}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {currentOrder.customer_name} • {currentOrder.marketplace || 'Manual'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="border-gray-700 h-8"
                >
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={cancelPicking}
                  className="border-red-500/50 text-red-400 h-8"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
              </div>
            </div>

            {/* Input de Código de Barras */}
            <div className="relative">
              <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
              <Input
                ref={barcodeInputRef}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={handleBarcodeScan}
                placeholder="📦 Bipe ou digite o código de barras (EAN/SKU)..."
                className="pl-10 pr-36 h-14 text-lg bg-gray-900 border-orange-500 focus:ring-orange-500"
                autoFocus
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
                <Button
                  onClick={() => setShowCameraScanner(true)}
                  className="bg-blue-500 hover:bg-blue-600 h-10"
                  title="Abrir câmera"
                >
                  <Camera className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => processScan(barcodeInput)}
                  disabled={!barcodeInput.trim()}
                  className="bg-orange-500 hover:bg-orange-600 h-10"
                >
                  <ScanLine className="w-4 h-4 mr-2" />
                  Bipar
                </Button>
              </div>
            </div>

            {/* Progresso */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-400 text-sm">Progresso da Separação</span>
                <span className="text-white font-bold">
                  {currentOrder.picked_items || 0} / {currentOrder.total_items} itens
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-orange-500 to-green-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${((currentOrder.picked_items || 0) / currentOrder.total_items) * 100}%` }}
                />
              </div>
            </div>
          </Card>

          {/* Grid: Itens do Pedido + Histórico */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Itens a Separar */}
            <Card className="bg-gray-800/50 border-gray-700 p-4">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-400" />
                Itens do Pedido
              </h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {currentOrder.items?.map((item, idx) => {
                  const isComplete = item.picked_qty >= item.quantity;
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        isComplete
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-gray-700/50 border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className={`font-medium ${isComplete ? 'text-green-400' : 'text-white'}`}>
                            {isComplete && <Check className="w-4 h-4 inline mr-1" />}
                            {item.product_name}
                          </p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                              SKU: {item.product_sku}
                            </span>
                            {item.product_ean && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                                EAN: {item.product_ean}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${isComplete ? 'text-green-400' : 'text-white'}`}>
                            {item.picked_qty || 0}/{item.quantity}
                          </p>
                          <p className="text-xs text-gray-500">unidades</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Histórico de Bipagens */}
            <Card className="bg-gray-800/50 border-gray-700 p-4">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <History className="w-5 h-5 text-purple-400" />
                Histórico de Bipagens
              </h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {scanHistory.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    Nenhuma bipagem ainda. Escaneie um produto!
                  </p>
                ) : (
                  scanHistory.map((scan, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg text-sm ${
                        scan.success
                          ? 'bg-green-500/10 border border-green-500/30'
                          : 'bg-red-500/10 border border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={scan.success ? 'text-green-400' : 'text-red-400'}>
                          {scan.message}
                        </span>
                        <span className="text-gray-500 text-xs">{scan.timestamp}</span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1">Código: {scan.barcode}</p>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Botão Finalizar */}
          {(currentOrder.picked_items || 0) >= currentOrder.total_items && (
            <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <h3 className="text-green-400 font-bold">Todos os itens foram separados!</h3>
                    <p className="text-gray-400 text-sm">Clique para finalizar e marcar como SEPARADO</p>
                  </div>
                </div>
                <Button
                  onClick={finishPicking}
                  className="bg-green-500 hover:bg-green-600 h-12 px-6"
                  disabled={loading}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Finalizar Separação
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Lista de Pedidos */}
      {activeView === 'orders' && (
        <>
          {/* Filtros */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-2">
              {['', 'A_SEPARAR', 'SEPARADO', 'ENVIADO'].map(status => (
                <Button
                  key={status}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={`h-8 ${statusFilter === status ? 'bg-orange-500' : 'bg-gray-700'}`}
                >
                  {status === '' ? 'Todos' : status.replace('_', ' ')}
                </Button>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadOrders}
              className="border-gray-700 h-8 ml-auto"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {/* Lista */}
          <div className="space-y-3">
            {orders.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700 p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">Nenhum pedido encontrado</p>
                <p className="text-gray-500 text-sm">Os pedidos aparecerão aqui quando forem criados</p>
              </Card>
            ) : (
              orders.map(order => (
                <Card key={order.id} className="bg-gray-800/50 border-gray-700 p-4 hover:border-orange-500/50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Info Principal */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-bold">#{order.order_number}</span>
                        {getStatusBadge(order.status)}
                        {order.marketplace && (
                          <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                            {order.marketplace}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">
                        {order.customer_name} • {order.customer_phone}
                      </p>
                    </div>

                    {/* Itens */}
                    <div className="lg:w-32 text-center">
                      <p className="text-xs text-gray-500">Itens</p>
                      <p className="text-white font-bold">
                        {order.picked_items || 0}/{order.total_items}
                      </p>
                    </div>

                    {/* Data */}
                    <div className="lg:w-36">
                      <p className="text-xs text-gray-500">Criado em</p>
                      <p className="text-gray-300 text-sm">
                        {new Date(order.created_at).toLocaleString('pt-BR')}
                      </p>
                    </div>

                    {/* Ações */}
                    <div className="flex gap-2">
                      {order.status === 'A_SEPARAR' && (
                        <Button
                          onClick={() => startPicking(order)}
                          className="bg-orange-500 hover:bg-orange-600 h-9"
                          disabled={loading}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Separar
                        </Button>
                      )}
                      {order.status === 'SEPARADO' && (
                        <Button
                          onClick={() => shipOrder(order.id)}
                          className="bg-purple-500 hover:bg-purple-600 h-9"
                        >
                          <Truck className="w-4 h-4 mr-1" />
                          Enviar
                        </Button>
                      )}
                      {order.status === 'ENVIADO' && order.tracking_code && (
                        <span className="text-purple-400 text-sm flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {order.tracking_code}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Lista de Itens (expandida) */}
                  {order.status === 'A_SEPARAR' && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <p className="text-xs text-gray-500 mb-2">Itens do pedido:</p>
                      <div className="flex flex-wrap gap-2">
                        {order.items?.map((item, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300"
                          >
                            {item.quantity}x {item.product_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* Modal de Scanner de Câmera */}
      <BarcodeScanner 
        isOpen={showCameraScanner} 
        onClose={() => setShowCameraScanner(false)}
        onScan={handleCameraScan}
      />
    </div>
  );
};

export default WMSPanel;
